/**
 * Extension Auth Sync Hook
 *
 * Enables bidirectional authentication sync between the Chrome extension
 * and the web app. When one is logged in, the other should be too.
 *
 * Flow:
 * 1. Web app → Extension: Handled by auth sync in the auth store
 * 2. Extension → Web app: This hook listens for EXTENSION_AUTH_STATE messages
 *
 * When extension has auth but web app doesn't:
 * - If on login page: Can auto-trigger sign-in or show "Sign in with extension" option
 * - If on protected page: Redirect to login with extension auth hint
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  onExtensionAuthState,
  ExtensionAuthState,
  requestAuthStateFromExtension,
} from '@/utils/extensionCommunication';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import { errorReporter } from '@/services/errorReportingService';
import { logger } from '@/services/loggerService';
import { isLogoutInProgress } from '@/utils/crossTabAuthSync';

interface ExtensionAuthSyncState {
  /** Whether extension has valid auth */
  extensionHasAuth: boolean;
  /** Extension user info (if authenticated) */
  extensionUser: ExtensionAuthState['user'] | null;
  /** Whether we're checking extension auth status */
  isChecking: boolean;
  /** Error message if sync failed */
  error: string | null;
  /** Attempt to sign in using extension's auth */
  signInWithExtension: () => Promise<boolean>;
}

/**
 * Hook to sync authentication state with the Chrome extension
 *
 * @example
 * ```tsx
 * const { extensionHasAuth, signInWithExtension } = useExtensionAuthSync();
 *
 * // On login page, offer to sign in with extension
 * if (extensionHasAuth && !userLoggedIn) {
 *   return <button onClick={signInWithExtension}>Sign in with Extension</button>;
 * }
 * ```
 */
export const useExtensionAuthSync = (): ExtensionAuthSyncState => {
  const { userLoggedIn } = useAuthStore();
  const [extensionAuthState, setExtensionAuthState] = useState<ExtensionAuthState | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen for extension auth state
  useEffect(() => {
    setIsChecking(true);
    let receivedResponse = false;

    const cleanup = onExtensionAuthState((authState) => {
      receivedResponse = true;
      setExtensionAuthState(authState);
      setIsChecking(false);
    });

    // Set a timeout for checking - if no response, extension probably not installed
    const timeoutId = setTimeout(() => {
      if (!receivedResponse) {
        setIsChecking(false);
      }
    }, 2000);

    return () => {
      cleanup();
      clearTimeout(timeoutId);
    };
  }, []);

  // Request extension auth state periodically or when web app auth changes
  useEffect(() => {
    if (!userLoggedIn) {
      // If web app isn't logged in, check if extension has auth
      requestAuthStateFromExtension();
    }
  }, [userLoggedIn]);

  /**
   * Attempt to sign in the web app using the extension's auth token
   *
   * This works by:
   * 1. Taking the extension's Firebase ID token
   * 2. Sending it to the backend to request a custom token
   * 3. Signing in with the custom token
   */
  const signInWithExtension = useCallback(async (): Promise<boolean> => {
    // CRITICAL: Check logout flag FIRST to prevent race condition
    // This is the first line of defense against auto-login during logout.
    // Checks the durable sessionStorage flag AND the cross-tab timestamp — the
    // timestamp alone expires after 10s even if logout never finished (CONTRACT #1).
    if (isLogoutInProgress()) {
      logger.debug('extension', 'Sign-in with extension blocked: logout was requested');
      return false;
    }
    if (!extensionAuthState?.token) {
      setError('No extension auth available');
      return false;
    }

    try {
      setError(null);

      // Request custom token from backend
      const apiBaseUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiBaseUrl}/api/auth/exchange-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${extensionAuthState.token}`,
        },
        body: JSON.stringify({
          source: 'extension',
        }),
      });

      if (!response.ok) {
        // Endpoint not deployed yet or other error
        const errorMsg =
          response.status === 404
            ? 'Token exchange not available yet. Please sign in manually.'
            : 'Token exchange failed';
        setError(errorMsg);
        return false;
      }

      const data = await response.json();
      const customToken = data?.customToken;

      if (typeof customToken !== 'string' || !customToken) {
        setError('Invalid token received from server');
        return false;
      }

      // Sign in with the custom token
      await signInWithCustomToken(auth, customToken);

      return true;
    } catch (err) {
      // Report error with appropriate severity based on type
      const isNetworkError =
        err instanceof Error &&
        (err.message.includes('fetch') || err.message.includes('network') || err.message.includes('Network'));

      if (isNetworkError) {
        errorReporter.expected('Extension auth token exchange failed - network error', err, {
          feature: 'extension',
          operation: 'signInWithExtension',
        });
      } else {
        errorReporter.critical('Extension auth token exchange failed unexpectedly', err, {
          feature: 'extension',
          operation: 'signInWithExtension',
        });
      }
      setError(err instanceof Error ? err.message : 'Failed to sign in with extension');
      return false;
    }
  }, [extensionAuthState]);

  return {
    extensionHasAuth: !!extensionAuthState?.hasAuth,
    extensionUser: extensionAuthState?.user || null,
    isChecking,
    error,
    signInWithExtension,
  };
};

export default useExtensionAuthSync;
